import type { SSOLoginConnectionInfo } from '@compass/client/types/auth';
import { NonAuthSidebar } from '@components/layouts';
import { useForgotPassword, useLogin } from '@hooks/useAuth';
import { getErrorMessage, getErrorTitle } from '@utils/errorMessages';
import {
  getEmailErrorMessage,
  getPasswordErrorMessage,
  isValidEmail,
  normalizeEmail,
} from '@utils/validation';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, ArrowLeft, Check, CheckCircle, Eye, EyeOff, Loader2, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { MfaVerifyForm } from '@/components/mfa/MfaVerifyForm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  stashSsoRedirect,
  useGetSsoConnectionByDomain,
  useStartSsoLogin,
  useStartSsoLoginByConnection,
} from '@/hooks/useSso';

type LoginStep = 'email' | 'method';

// Official Google "G" 4-color mark, per Google's Sign-in branding guidelines.
// Do not recolor, resize, or otherwise modify.
function GoogleLogoIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}

// Google's branding guidelines prohibit showing the Google mark for non-Google
// IdPs. Detect Google by the connection name or a gmail/googlemail domain —
// a heuristic until the backend exposes a `provider_type` enum.
function isGoogleSsoConnection(connection?: SSOLoginConnectionInfo): boolean {
  if (!connection) return false;
  if ((connection.name || '').toLowerCase().includes('google')) return true;
  return (connection.allowed_domains || []).some(d => {
    const domain = d.toLowerCase();
    return domain === 'gmail.com' || domain === 'googlemail.com';
  });
}

export function LoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loginStep, setLoginStep] = useState<LoginStep>('email');
  const [emailValue, setEmailValue] = useState<string>('');
  const [passwordValue, setPasswordValue] = useState<string>('');
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const loginMutation = useLogin();
  const params = new URLSearchParams(window.location.search);
  const invitation = params.get('invitation');
  const organization = params.get('organization');

  // Result of the pre-auth domain lookup — populated when the user advances
  // from the email step. Empty array means "no SSO connection for this domain".
  const [discoveredConnections, setDiscoveredConnections] = useState<SSOLoginConnectionInfo[]>([]);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState<string>('');
  const [forgotPasswordError, setForgotPasswordError] = useState<string>('');
  const [isForgotPasswordSubmitted, setIsForgotPasswordSubmitted] = useState<boolean>(false);
  const forgotPasswordInputRef = useRef<HTMLInputElement>(null);
  const forgotPasswordMutation = useForgotPassword();

  // SSO state
  const [ssoError, setSsoError] = useState<string>('');
  const startSsoLogin = useStartSsoLogin();
  const getSsoByDomain = useGetSsoConnectionByDomain();
  const startSsoLoginByConnection = useStartSsoLoginByConnection();

  // Extract org identifier from subdomain or query param.
  //
  // Resolution order:
  // 1. ?org_id=<mongodb_id>  — direct org ObjectID (local dev / deep links)
  // 2. ?org=<slug>           — org slug (local dev: /login?org=100ms)
  // 3. Subdomain             — "orgslug.compass.health" → "orgslug" (production)
  //
  // If none found, SSO button is hidden.
  const ssoOrgSlug = (() => {
    const params = new URLSearchParams(window.location.search);
    // Direct org ID (MongoDB ObjectID)
    const orgIdParam = params.get('org_id');
    if (orgIdParam) return orgIdParam;
    // Org slug
    const orgParam = params.get('org');
    if (orgParam) return orgParam;

    // Org subdomain: {org}.{env}-dash.100ms.ai → org (4 parts)
    // Dashboard base hostnames (dev-dash.100ms.ai, dash.100ms.ai) have 3 parts and are NOT org subdomains.
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return null;
    const parts = hostname.split('.');
    if (parts.length < 4) return null;
    return parts[0];
  })();

  // Derived view state for the method step
  const ssoConnection = discoveredConnections.find(c => c.is_default) ?? discoveredConnections[0];
  const hasDomainSso = !!ssoConnection?.connection_id;
  const hasSso = hasDomainSso || !!ssoOrgSlug;
  const isSsoEnforced = !!ssoConnection?.sso_enforced;
  // Password is hidden only when the connection has sso_enforced=true (backend-
  // controlled per connection). When SSO is configured but not enforced (e.g. dev),
  // both the SSO button and password login are shown.
  const showPasswordOnMethodStep = !isSsoEnforced;
  const isGoogleSso = isGoogleSsoConnection(ssoConnection);
  const ssoInProgress = startSsoLogin.isPending || startSsoLoginByConnection.isPending;

  // Advance from the email step to the method step.
  // When an org slug is known (subdomain/query) we skip the domain lookup and
  // rely on the org-scoped StartSSOLogin call. Otherwise we look up connections
  // by email domain and render the method step based on the result.
  const handleEmailContinue = useCallback(
    (e?: React.FormEvent | React.MouseEvent) => {
      e?.preventDefault();

      const email = normalizeEmail(emailInputRef.current?.value || emailValue);
      const emailError = getEmailErrorMessage(email);
      if (emailError) {
        setErrorMessage(emailError);
        return;
      }

      setEmailValue(email);
      setErrorMessage('');
      setSsoError('');
      loginMutation.reset();

      if (ssoOrgSlug) {
        setDiscoveredConnections([]);
        setLoginStep('method');
        return;
      }

      const domain = email.split('@')[1]?.toLowerCase();
      if (!domain) {
        setErrorMessage('Please enter a valid work email.');
        return;
      }

      getSsoByDomain.mutate(domain, {
        onSuccess: data => {
          setDiscoveredConnections(data.connections ?? []);
          setLoginStep('method');
        },
        onError: () => {
          // Graceful fallback: advance to method step with no SSO available,
          // so password login renders on its own.
          setDiscoveredConnections([]);
          setLoginStep('method');
        },
      });
    },
    [emailValue, ssoOrgSlug, getSsoByDomain, loginMutation],
  );

  const handleBackToEmail = useCallback(() => {
    setLoginStep('email');
    setDiscoveredConnections([]);
    setPasswordValue('');
    setSsoError('');
    setErrorMessage('');
    loginMutation.reset();
  }, [loginMutation]);

  // Submit password (or passwordless-email) on the method step.
  const handleLogin = useCallback(
    (e?: React.FormEvent | React.MouseEvent) => {
      e?.preventDefault();

      const password = (passwordInputRef.current?.value || '').trim();
      const passwordError = getPasswordErrorMessage(password);
      if (passwordError) {
        setErrorMessage(passwordError);
        return;
      }
      loginMutation.mutate({ email: emailValue, password });
    },
    [emailValue, loginMutation],
  );

  useEffect(() => {
    // If invitation params are present, redirect to signup page
    if (invitation && organization) {
      navigate(
        `/signup?invitation=${encodeURIComponent(invitation)}&organization=${encodeURIComponent(organization)}`,
        { replace: true },
      );
    }
  }, [invitation, organization, navigate]);

  // Handle forgot password form submission
  const handleForgotPasswordSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      const email = normalizeEmail(forgotPasswordInputRef.current?.value || '');

      // Validate email
      const emailError = getEmailErrorMessage(email);
      if (emailError) {
        setForgotPasswordError(emailError);
        return;
      }

      setForgotPasswordError('');

      forgotPasswordMutation.mutate(
        { email },
        {
          onSuccess: () => {
            setIsForgotPasswordSubmitted(true);
          },
          onError: (err: any) => {
            const apiMessage = err?.response?.data?.message || err?.response?.data?.error;
            setForgotPasswordError(
              apiMessage || err.message || 'Failed to send reset email. Please try again.',
            );
          },
        },
      );
    },
    [forgotPasswordMutation],
  );

  // Handle opening forgot password panel
  const handleOpenForgotPassword = useCallback(() => {
    setShowForgotPassword(true);
    setForgotPasswordEmail(emailValue);
  }, [emailValue]);

  // Handle closing forgot password panel
  const handleCloseForgotPassword = useCallback(() => {
    setShowForgotPassword(false);
    setForgotPasswordEmail('');
    setForgotPasswordError('');
    setIsForgotPasswordSubmitted(false);
    forgotPasswordMutation.reset();
  }, [forgotPasswordMutation]);

  // Start SSO login from the method step. Uses the already-discovered connection
  // (from handleEmailContinue) or falls back to the org-slug flow when an org
  // was derived from the subdomain/query param.
  const handleSsoLogin = useCallback(() => {
    setSsoError('');

    // SSO does a full-page redirect to the IdP, which drops react-router state and
    // query params. Persist the intended destination now (query param first, then the
    // location the auth guard captured) so SSOCallback can return the user there.
    const ssoParams = new URLSearchParams(window.location.search);
    stashSsoRedirect(ssoParams.get('redirect') || location.state?.from?.pathname);

    const onStartSuccess = (data: { authorization_url?: string }) => {
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        setSsoError('SSO is not configured for this organization.');
      }
    };
    const onStartError = (err: any) => {
      const apiMessage = err?.response?.data?.message || err?.response?.data?.error;
      setSsoError(apiMessage || 'SSO is not configured for this organization.');
    };

    if (ssoConnection?.connection_id) {
      startSsoLoginByConnection.mutate(ssoConnection.connection_id, {
        onSuccess: onStartSuccess,
        onError: onStartError,
      });
      return;
    }

    if (ssoOrgSlug) {
      startSsoLogin.mutate(ssoOrgSlug, { onSuccess: onStartSuccess, onError: onStartError });
      return;
    }

    setSsoError('SSO is not configured for this organization.');
  }, [
    ssoConnection,
    ssoOrgSlug,
    startSsoLogin,
    startSsoLoginByConnection,
    location.state?.from?.pathname,
  ]);

  return (
    <div className="h-screen w-full flex">
      <NonAuthSidebar />
      <div className="flex-1 flex items-center justify-center p-8 overflow-hidden">
        <AnimatePresence mode="wait">
          {loginMutation.mfaState?.required ? (
            <MfaVerifyForm
              challengeToken={loginMutation.mfaState.challengeToken}
              methods={loginMutation.mfaState.methods}
              onSuccess={(user, tokenPair) => loginMutation.completeLogin(user, tokenPair)}
              onBack={() => loginMutation.clearMfaState()}
            />
          ) : !showForgotPassword && loginStep === 'email' ? (
            /* Step 1: Email entry */
            <motion.div
              key="login-email"
              initial={{ opacity: 1, x: 0 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="w-full max-w-md space-y-8"
            >
              <div className="space-y-2">
                <h2 className="text-3xl font-semibold">Login</h2>
                <p className="text-muted-foreground">Enter your work email to continue.</p>
              </div>
              <form onSubmit={handleEmailContinue} className="space-y-6">
                <div className="relative">
                  <Input
                    ref={emailInputRef}
                    value={emailValue}
                    placeholder="Enter your work email"
                    type="email"
                    autoFocus
                    className={`w-full h-12 px-4 pr-10 ${errorMessage ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                    onChange={e => {
                      setEmailValue(e.target.value);
                      if (errorMessage) setErrorMessage('');
                    }}
                  />
                  {emailValue && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {isValidEmail(emailValue) ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <X className="h-4 w-4 text-destructive" />
                      )}
                    </div>
                  )}
                </div>

                {errorMessage && (
                  <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                      <p className="flex-1">{errorMessage}</p>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-12"
                  disabled={!isValidEmail(emailValue) || getSsoByDomain.isPending}
                >
                  {getSsoByDomain.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </form>
            </motion.div>
          ) : !showForgotPassword && loginStep === 'method' ? (
            /* Step 2: Choose login method based on discovery result */
            <motion.div
              key="login-method"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="w-full max-w-md space-y-8"
            >
              <div className="space-y-2">
                <h2 className="text-3xl font-semibold">Welcome back</h2>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="truncate">{emailValue}</span>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={handleBackToEmail}
                    className="h-auto p-0 text-primary"
                  >
                    Not you?
                  </Button>
                </div>
              </div>

              {hasSso && (
                <div className="space-y-3">
                  {isSsoEnforced && (
                    <div className="bg-muted border border-border px-4 py-3 rounded-md text-sm">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 mt-0.5 shrink-0 text-primary" />
                        <p className="flex-1 text-muted-foreground">
                          Your organization requires Single Sign-On.
                        </p>
                      </div>
                    </div>
                  )}
                  {isGoogleSso ? (
                    // Google-branded button per https://developers.google.com/identity/branding-guidelines
                    // Light theme: #FFFFFF fill, 1px #747775 stroke, #1F1F1F text.
                    // Uses literal hex values (not shadcn tokens) because the branding
                    // guidelines require these exact colors.
                    <button
                      type="button"
                      disabled={ssoInProgress}
                      onClick={handleSsoLogin}
                      className="w-full h-12 flex items-center justify-center gap-2.5 rounded-md border border-[#747775] bg-[#FFFFFF] px-3 text-sm font-medium text-[#1F1F1F] transition-colors hover:bg-[#F8F9FA] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {ssoInProgress ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Connecting...</span>
                        </>
                      ) : (
                        <>
                          <GoogleLogoIcon />
                          <span>Continue with Google</span>
                        </>
                      )}
                    </button>
                  ) : (
                    <Button
                      type="button"
                      className="w-full h-12"
                      disabled={ssoInProgress}
                      onClick={handleSsoLogin}
                    >
                      {ssoInProgress ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        `Continue with ${ssoConnection?.name || 'SSO'}`
                      )}
                    </Button>
                  )}
                  {ssoError && (
                    <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                        <p className="flex-1">{ssoError}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {hasSso && showPasswordOnMethodStep && (
                <div className="flex items-center gap-3">
                  <Separator className="flex-1" />
                  <span className="text-sm text-muted-foreground">or</span>
                  <Separator className="flex-1" />
                </div>
              )}

              {showPasswordOnMethodStep && (
                <form onSubmit={handleLogin} className="space-y-6">
                  <div className="space-y-2">
                    <div className="relative">
                      <Input
                        ref={passwordInputRef}
                        value={passwordValue}
                        placeholder="Enter your password"
                        type={showPassword ? 'text' : 'password'}
                        autoFocus={!hasSso}
                        className={`w-full h-12 px-4 pr-10 ${loginMutation.isError || errorMessage ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                        onChange={e => {
                          setPasswordValue(e.target.value);
                          if (loginMutation.isError || errorMessage) {
                            loginMutation.reset();
                            setErrorMessage('');
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={handleOpenForgotPassword}
                        className="h-auto p-0"
                      >
                        Forgot Password?
                      </Button>
                    </div>
                  </div>

                  {(loginMutation.isError || errorMessage) && (
                    <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <p className="font-medium">
                            {loginMutation.error
                              ? getErrorTitle(loginMutation.error, 'Login Failed')
                              : 'Login Failed'}
                          </p>
                          <p className="mt-1">
                            {errorMessage ||
                              (loginMutation.error ? getErrorMessage(loginMutation.error) : '')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    variant={hasSso ? 'outline' : 'default'}
                    className="w-full h-12"
                    disabled={passwordValue.length === 0 || loginMutation.isPending}
                  >
                    {loginMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Logging in...
                      </>
                    ) : (
                      'Sign in with password'
                    )}
                  </Button>
                </form>
              )}

              <Button
                type="button"
                variant="link"
                onClick={handleBackToEmail}
                className="w-full h-auto p-0 text-muted-foreground"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Use a different email
              </Button>
            </motion.div>
          ) : (
            /* Forgot Password Form */
            <motion.div
              key="forgot-password"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="w-full max-w-md"
            >
              {/* Success State */}
              {isForgotPasswordSubmitted ? (
                <div className="space-y-8">
                  <div className="text-center space-y-4">
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 200,
                        damping: 15,
                        delay: 0.1,
                      }}
                      className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center"
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{
                          type: 'spring',
                          stiffness: 200,
                          damping: 10,
                          delay: 0.3,
                        }}
                      >
                        <CheckCircle className="h-8 w-8 text-green-600" />
                      </motion.div>
                    </motion.div>
                    <h2 className="text-3xl font-semibold">Check your inbox</h2>
                    <p className="text-muted-foreground">
                      We have sent a password reset link to{' '}
                      <span className="font-medium text-foreground">{forgotPasswordEmail}</span>.
                      Check your inbox and follow the instructions to reset your password.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <Button className="w-full h-12" onClick={handleCloseForgotPassword}>
                      Back to Login
                    </Button>

                    <p className="text-center text-sm text-muted-foreground">
                      Didn&apos;t receive the email?{' '}
                      <Button
                        type="button"
                        variant="link"
                        onClick={() => {
                          setIsForgotPasswordSubmitted(false);
                          forgotPasswordMutation.reset();
                        }}
                        className="h-auto p-0 text-primary"
                      >
                        Try again
                      </Button>
                    </p>
                  </div>
                </div>
              ) : (
                /* Form State */
                <>
                  <div className="space-y-2 mb-8">
                    <h2 className="text-3xl font-semibold">Forgot Password</h2>
                    <p className="text-muted-foreground">
                      Enter your email address and we&apos;ll send you a link to reset your
                      password.
                    </p>
                  </div>

                  <form onSubmit={handleForgotPasswordSubmit} className="space-y-6">
                    {/* Email Field */}
                    <div className="space-y-2">
                      <label htmlFor="forgot-email" className="text-sm font-medium">
                        Email
                      </label>
                      <div className="relative">
                        <Input
                          id="forgot-email"
                          ref={forgotPasswordInputRef}
                          value={forgotPasswordEmail}
                          placeholder="Enter your work email"
                          type="email"
                          className={`w-full h-12 px-4 pr-10 ${
                            forgotPasswordMutation.isError || forgotPasswordError
                              ? 'border-destructive focus-visible:ring-destructive'
                              : ''
                          }`}
                          onChange={e => {
                            setForgotPasswordEmail(e.target.value);
                            setForgotPasswordError('');
                            forgotPasswordMutation.reset();
                          }}
                        />
                        {forgotPasswordEmail && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            {isValidEmail(forgotPasswordEmail) ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <X className="h-4 w-4 text-destructive" />
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Error message display */}
                    {(forgotPasswordMutation.isError || forgotPasswordError) && (
                      <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                          <div className="flex-1">
                            <p className="font-medium">Request Failed</p>
                            <p className="mt-1">
                              {forgotPasswordError ||
                                'Failed to send reset email. Please try again.'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="space-y-2 pt-4">
                      <Button
                        type="submit"
                        className="w-full h-12"
                        disabled={
                          !isValidEmail(forgotPasswordEmail) || forgotPasswordMutation.isPending
                        }
                      >
                        {forgotPasswordMutation.isPending ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          'Send password reset link'
                        )}
                      </Button>

                      <Button
                        type="button"
                        variant="link"
                        className="w-full h-12"
                        onClick={handleCloseForgotPassword}
                      >
                        Back to Login
                      </Button>
                    </div>
                  </form>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
